import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import IssuerDashboardNotificationItem from '@/modules/issuer-dashboard/components/items/IssuerDashboardNotificationItem.tsx';
import { useIssuerNotificationsPageQuery } from '@/modules/issuer-dashboard/queries/event-logs.ts';
import { ChartEmptyState, InfoCard, Spinner } from 'ui';
import { ArrowLeftGayIcon, ArrowLeftIcon } from 'ui/icons';

const DEFAULT_ITEMS_PER_PAGE = 1;
const NOTIFICATION_GAP_PX = 16;
const FALLBACK_NOTIFICATION_HEIGHT_PX = 56;

const Notifications = () => {
  const [params] = useSearchParams();
  const registerId = params.get(REGISTER_ID_SEARCH_PARAM_KEY) || '';
  const [page, setPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setPage(1);
  }, [registerId]);

  const { data, isFetching, isLoading } = useIssuerNotificationsPageQuery(
    { page, page_size: itemsPerPage },
    registerId,
  );

  const notifications = data?.data || [];
  const totalItems = data?.total || 0;

  useEffect(() => {
    const listElement = listRef.current;
    if (!listElement) return;

    const getNotificationItems = () =>
      Array.from(listElement.querySelectorAll<HTMLDivElement>('[data-notification-item]'));

    const updateItemsPerPage = () => {
      const notificationItems = getNotificationItems();
      const itemHeight = Math.max(
        FALLBACK_NOTIFICATION_HEIGHT_PX,
        ...notificationItems.map(notificationItem => notificationItem.offsetHeight),
      );
      const nextItemsPerPage = Math.max(
        1,
        Math.floor(
          (listElement.clientHeight + NOTIFICATION_GAP_PX) / (itemHeight + NOTIFICATION_GAP_PX),
        ),
      );

      setItemsPerPage(currentItemsPerPage =>
        currentItemsPerPage === nextItemsPerPage ? currentItemsPerPage : nextItemsPerPage,
      );
    };

    const observer = new ResizeObserver(updateItemsPerPage);
    observer.observe(listElement);
    getNotificationItems().forEach(notificationItem => observer.observe(notificationItem));
    updateItemsPerPage();

    return () => observer.disconnect();
  }, [notifications]);

  useEffect(() => {
    setPage(1);
  }, [itemsPerPage]);

  const firstItemShownNumber = (page - 1) * itemsPerPage + 1;
  const lastItemShownNumber = Math.min((page - 1) * itemsPerPage + itemsPerPage, totalItems);

  const isPossibleGoBack = !isFetching && page > 1;
  const isPossibleGoForward = !isFetching && lastItemShownNumber < totalItems;

  const goToPreviousPage = useCallback(() => {
    setPage(page => page - 1);
  }, []);

  const goToNextPage = useCallback(() => {
    setPage(page => page + 1);
  }, []);

  const bottomContent = !!notifications.length && (
    <div className="w-full flex flex-row justify-between">
      <div className="text-sm text-neutral-500">
        {firstItemShownNumber} - {lastItemShownNumber} of {totalItems}
      </div>

      <div className="flex flex-row gap-6">
        {isFetching && <Spinner size="sm" />}
        <button onClick={goToPreviousPage} disabled={!isPossibleGoBack}>
          <img alt="back" src={isPossibleGoBack ? ArrowLeftIcon : ArrowLeftGayIcon} />
        </button>
        <button onClick={goToNextPage} disabled={!isPossibleGoForward}>
          <img
            alt="forward"
            src={isPossibleGoForward ? ArrowLeftIcon : ArrowLeftGayIcon}
            className="rotate-180"
          />
        </button>
      </div>
    </div>
  );

  return (
    <InfoCard
      title="Event logs"
      bottomAction={bottomContent}
      classes={{
        block: 'h-full min-h-[250px] [contain:size]',
        content: 'flex-1 min-h-0 overflow-hidden',
      }}
    >
      <div ref={listRef} className="w-full h-full min-h-0 overflow-hidden">
        {isLoading && (
          <div className="flex justify-center items-center font-medium text-lg">
            <ChartEmptyState isLoading={isLoading} />
          </div>
        )}
        {!isLoading && !notifications.length && (
          <div className="w-full h-full flex items-center justify-center">
            <p className="text-lg font-semibold text-center">
              Notifications about actions happening in your registry will appear here
            </p>
          </div>
        )}
        {!isLoading && !!notifications.length && (
          <div className="flex flex-col gap-4">
            {notifications.map(notification => (
              <div key={notification.id} data-notification-item>
                <IssuerDashboardNotificationItem notification={notification} />
              </div>
            ))}
          </div>
        )}
      </div>
    </InfoCard>
  );
};

export default Notifications;
